import {useQueries} from '@github-ui/react-query'
import {useCallback, useMemo, useState} from 'react'

import {useTimelineFocusQueryOptions} from './fetch-timeline-focus'
import {
  type TimelineFeed,
  type TimelineIssue,
  type TimelineItemsPageParams,
  timelineQueryOptions,
} from './fetch-timeline-items'

/**
 * # Reading the timeline in sections
 *
 * A long timeline is read as a few **sections** with gaps between them: the oldest comments, the
 * newest comments, and -- when the reader follows a permalink -- a group around the comment they
 * were sent to. Each section is one read of the timeline endpoint: params in, one result out.
 *
 * Every section is an ordinary observed query, including the pages "load more" opens. That is what
 * this file is for. It means invalidation, refetching and optimistic writes reach each of them the
 * same way, and every one of them keeps up with the server on its own.
 *
 * All the sections are read in a single `useQueries`, so the caller passes an array of sections and
 * gets an array of reads lined up with it. `select` picks out the sections of one role in the
 * order they render, and `baseIndex` finds the section a role starts from.
 *
 * A section's identity is its params, because that is what TanStack keys the cache by. Rows already
 * in the cache -- hydrated from the page payload, or read by an earlier "load more" -- are reused on
 * mount rather than refetched. Invalidation still reaches them.
 */

/** Which part of the timeline a section covers. A base section and its "load more" pages share one. */
export type SectionRole = 'front' | 'back' | 'highlight-before' | 'highlight' | 'highlight-after'

/** See the `staleTime` this feeds, below, for why the opening sections need a finite one. */
const OPENING_SECTION_STALE_TIME_MS = 60_000

export type TimelineSection = {
  /** `front` is the oldest end of the timeline, `back` the newest. */
  role: SectionRole
  params: TimelineItemsPageParams
  /**
   * One of the sections the timeline opens with, as opposed to a page "load more" added beside it.
   * There are at most three: the oldest window, the newest, and the permalink window.
   */
  isBase?: boolean
}

export type TimelineSections = {
  /**
   * What each section's read returned, in the order the sections were given.
   *
   * Each one is the issue with that section's slice of `timelineItems` on it. It is the issue and
   * not the bare connection because an optimistic write finds its connection by matching the owner
   * `id` inside the value it is applied to.
   */
  reads: Array<TimelineIssue | undefined>
  loading: boolean[]
  /** Picks the entries for one role out of any array lined up with the sections, in render order. */
  select: <T>(values: T[], role: SectionRole) => Array<T | undefined>
  /** -1 when the role has no base section, which reads as `undefined` from a lined-up array. */
  baseIndex: (role: SectionRole) => number
}

export function useTimelineSections({
  owner,
  repo,
  number,
  feed = 'activity',
  sections,
}: {
  owner: string
  repo: string
  number: number
  feed?: TimelineFeed
  sections: TimelineSection[]
}): TimelineSections {
  const focusOptions = useTimelineFocusQueryOptions({
    owner,
    repo,
    number,
    feed,
    params: sections.find(section => section.role === 'highlight')?.params,
  })
  const queries = useMemo(
    () =>
      sections.map(section => ({
        ...(section.role === 'highlight' && focusOptions
          ? focusOptions
          : timelineQueryOptions(owner, repo, number, section.params, feed)),
        // `isBase` marks the three sections the timeline opens with -- oldest, newest, and the
        // permalink window -- as opposed to the pages "load more" adds beside them.
        //
        // A "load more" page never goes stale: it holds rows it fetched itself, and re-reading them
        // on mount would only cost a request. Invalidation still reaches it, so it is not frozen.
        //
        // An opening section cannot have that. Its rows may have been put there by the page payload
        // rather than fetched, and those have to survive the mount that immediately follows or
        // storing them bought nothing -- but the hosts that never prefill still need theirs to go
        // stale, or closing an issue side panel and reopening it shows whatever it showed before and
        // never asks again. A minute covers the first and is short enough for the second.
        staleTime: section.isBase ? OPENING_SECTION_STALE_TIME_MS : Infinity,
        // Not the default here, twice over. TanStack turns `refetchOnReconnect` off entirely whenever
        // `networkMode` is `always`, which this app sets globally -- and `true` would only re-read a
        // window it already considers stale.
        //
        // Neither suits these. They are kept current by live updates, and a notification that
        // arrives while the connection is down is never redelivered, so what was missed has nothing
        // to do with how recently the rows were written. Reconnecting is the one moment we know to
        // look, hence `always`.
        refetchOnReconnect: section.isBase ? ('always' as const) : (false as const),
        // A failed read reaches the error boundary only when it leaves nothing to show: the oldest
        // section with no rows of its own. The newest section reads the end of every timeline, so
        // on a short issue it is re-reading rows the oldest already returned -- a transient failure
        // there must not take down a list that is rendering perfectly well, and would leave it down
        // until a reload. What a failed section would have held is already accounted for by the
        // "N remaining items" marker.
        throwOnError: (_error: unknown, query: {state: {data?: unknown}}) =>
          section.role === 'front' && Boolean(section.isBase) && query.state.data === undefined,
      })),
    [owner, repo, number, feed, sections, focusOptions],
  )

  const queryResults = useQueries({
    queries,
    combine: entries => ({
      reads: entries.map(entry => entry.data),
      loading: entries.map(entry => entry.isLoading),
    }),
  })

  const select = useCallback(
    <T>(values: T[], role: SectionRole) =>
      sections.flatMap((section, index) => (section.role === role ? [values[index]] : [])),
    [sections],
  )

  const baseIndex = useCallback(
    (role: SectionRole) => sections.findIndex(section => section.role === role && section.isBase),
    [sections],
  )

  return {
    reads: queryResults.reads,
    loading: queryResults.loading,
    select,
    baseIndex,
  }
}

/**
 * The pages "load more" has read, kept as the params that read them rather than the rows they
 * returned: rows are a snapshot, and a snapshot cannot follow an invalidation, so such a page would
 * keep showing a comment after someone deleted it.
 *
 * Each list is kept in the order the pages render, oldest first, which is the reverse of the order
 * they were read in on the newest side and before a permalink.
 */
export function useLoadedPages(
  /** Pages are kept per permalink, so soft-navigating between two does not mix them. */
  focusText: string | undefined,
) {
  const [front, setFront] = useState<TimelineItemsPageParams[]>([])
  const [back, setBack] = useState<TimelineItemsPageParams[]>([])
  const [highlightByFocus, setHighlightByFocus] = useState<Record<string, TimelineSection[]>>({})

  const highlight = useMemo(() => (focusText ? (highlightByFocus[focusText] ?? []) : []), [highlightByFocus, focusText])

  /** Reading forward from the oldest end, so each page renders after the ones before it. */
  const addFront = useCallback((params: TimelineItemsPageParams) => setFront(pages => [...pages, params]), [])

  /** Reading backward from the newest end, so each page renders before the ones already read. */
  const addBack = useCallback((params: TimelineItemsPageParams) => setBack(pages => [params, ...pages]), [])

  const addHighlight = useCallback(
    (direction: 'before' | 'after') => (params: TimelineItemsPageParams) => {
      if (!focusText) return
      const role: SectionRole = direction === 'before' ? 'highlight-before' : 'highlight-after'
      const page: TimelineSection = {role, params}
      setHighlightByFocus(byFocus => {
        const kept = byFocus[focusText] ?? []
        return {...byFocus, [focusText]: direction === 'before' ? [page, ...kept] : [...kept, page]}
      })
    },
    [focusText],
  )

  return {front, back, highlight, addFront, addBack, addHighlight}
}
